import React, { useCallback } from 'react'

import { useRemoteStrings } from '@acorns/web-utils'

import { useBlueprintBindings } from '../utils/blueprint-bindings-context'
import type { GroveBlueprintComponentProps } from '../utils/grove-blueprint-component-props'
import { resolveModifiers } from '../utils/resolve-modifiers'
import { resolveTextValue } from '../utils/resolve-text-value'

export function GroveBlueprintTextField({
  node,
}: GroveBlueprintComponentProps) {
  const { getRemoteString } = useRemoteStrings()
  const { bindingValues, bindingHandlers } = useBlueprintBindings()

  const bindingKey =
    node.type === 'TextField' ? node.props.value.binding : undefined

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (bindingKey === undefined) {
        return
      }

      const bindingHandler = bindingHandlers[bindingKey]

      if (bindingHandler !== undefined) {
        bindingHandler(event.target.value)
      } else {
        // deno-lint-ignore no-console
        console.error(`Missing binding handler for key: "${bindingKey}"`)
      }
    },
    [bindingKey, bindingHandlers],
  )

  /* v8 ignore if -- @preserve */
  if (node.type !== 'TextField') {
    return null
  }

  const { className, style, attributes } = resolveModifiers(
    node.modifiers,
    bindingValues,
    (key: string) => getRemoteString({ key }),
  )

  const placeholder =
    node.props.placeholder !== undefined
      ? resolveTextValue(
          node.props.placeholder,
          (key: string) => getRemoteString({ key }),
          bindingValues,
        )
      : undefined

  const inputValue = String(bindingValues[node.props.value.binding] ?? '')

  return (
    <div
      className={`border border-acorns-ivory-light px-4 py-3 flex w-full flex-col rounded-[10px] bg-acorns-ivory-light ${className}`.trim()}
      style={style}
      {...attributes}
    >
      <input
        className={
          'w-full bg-transparent text-body text-acorns-slate placeholder-acorns-stone focus:outline-none'
        }
        onChange={handleChange}
        placeholder={placeholder}
        type={'text'}
        value={inputValue}
      />
    </div>
  )
}
